import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import { isEmpty } from 'lodash';
import { Button } from '../../../components/ui/Button';
import { Tooltip } from 'primereact/tooltip';
import { useNavigate } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';

import Layout from '../../Layout/Layout';
import Update from '../Layouts/components/Setup/Update';
import { formatDuration, useTitle } from '../../../config/global';
import CreateAnimationLayout from './Create/CreateAnimationLayout';
import DeleteConfirmation from '../../SharedComponents/DeleteConfirmation';
import FeatureLocked from '../../SharedComponents/FeatureLocked';

import { useDeleteLayout, useGetAllLayouts, useUpdateLayout } from '../../../queries';
import { setSelectedLayout } from '../../../redux/Slices/layoutSlice';
import { toggleDeleteDialog, toggleDialog } from '../../../redux/Slices/Global/globalSlice';

function AnimatedLayoutIndex() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch()

    const scrollRef = useRef(null);
    const sentinelRef = useRef(null);
    const inputRefs = useRef({});

    const [titleValue, setTitleValue] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [layout, setLayout] = useState({ name: "" });
    const [loadingAll, setLoadingAll] = React.useState(false);
    const [editingItemId, setEditingItemId] = useState(null);

    const { openDialog, deleteDialog } = useSelector((state) => state.global);
    const updateLayout = useUpdateLayout();

    useTitle("Animated Layouts")
    const {
        data,
        isLoading,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        refetch,
        error
    } = useGetAllLayouts({
        per_page: 25,
        search: searchTerm.trim() || undefined
    });



    useEffect(() => {
        if (!sentinelRef.current) return;

        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            {
                root: scrollRef.current ?? null,  // observe the real scroller
                rootMargin: '600px 0px 400px 0px', // prefetch before bottom
                threshold: 0,
            }
        );

        io.observe(sentinelRef.current);
        return () => io.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                editingItemId &&
                inputRefs.current[editingItemId] &&
                !inputRefs.current[editingItemId].contains(e.target)
            ) {
                setEditingItemId(null);
                setTitleValue("");
            }
        };

        if (editingItemId) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [editingItemId]);

    const openEditDialog = useCallback((data) => {
        setLayout(data);
        dispatch(toggleDialog("updateLayout"))
    }, [dispatch])



    const confirmDeleteLayout = useCallback((data) => {
        setLayout(data)
        dispatch(setSelectedLayout(data))
        dispatch(toggleDeleteDialog("layout"));
    }, [dispatch])

    const actionBodyTemplate = useCallback((rowData) => {
        return (
            <div className="flex gap-2 text-nowrap">
                {/* Setup page */}
                <Tooltip target={`.setup-button-${rowData.id}`} showDelay={100} className="fs-8" />
                <button className={`btn btn-sm btn-icon setup-button-${rowData.id}`} data-pr-position="bottom" data-pr-tooltip={t('animated_layouts.tooltip_setup')} onClick={() => navigate('/apps/animated_layouts/' + rowData.id + '/show')}><i className='bx bx-slideshow me-1'></i></button>
                {/* Update layout */}
                <Tooltip target={`.update-button-${rowData.id}`} showDelay={100} className="fs-8" />
                <button className={`btn btn-sm btn-icon update-button-${rowData.id}`} data-pr-position="bottom" data-pr-tooltip={t('animated_layouts.tooltip_update')} onClick={() => openEditDialog(rowData)}><i className="pi pi-pencil"></i></button>

                {/* Delete layout */}
                <Tooltip target={`.delete-button-${rowData.id}`} showDelay={100} className="fs-8" />
                <button className={`btn btn-sm text-red-500 btn-icon delete-button-${rowData.id}`} data-pr-position="bottom" data-pr-tooltip={t('animated_layouts.tooltip_delete')} onClick={() => confirmDeleteLayout(rowData)} ><i className="bx bx-trash text-red-600"></i></button>
            </div>
        )
    }, [navigate, openEditDialog, confirmDeleteLayout])

    const handleSubmitTag = async (item, titleValue, key) => {

        await updateLayout.mutateAsync({
            layoutId: item.id,
            data: {
                "status": item.status,
                "title": titleValue,
            },
        }, {
            onSuccess: () => {
                refetch()
                setEditingItemId(null);
                setTitleValue("");
            }
        })

    }

    const { mutate: deleteOneLayout } = useDeleteLayout()
    const deleteLayout = () => {
        deleteOneLayout(layout.id, {
            onSuccess: () => {
                refetch();
            }
        })
    }



    const handleInput = () => setSearchTerm(searchTerm.trim());
    const resetInput = () => setSearchTerm("");
    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleInput();
        }
    };

    const subNavbarContent = useMemo(() => {
        return (
            <div className={`flex justify-end items-center w-100`}>
                <div className="flex-grow-1 me-2">
                    <div className="relative flex w-full justify-start pb-2 pt-2  ">
                        <input
                            type="text"
                            className="form-control h-[45px] w-8/12 md:w-10/12 hover:border-blue-500"
                            placeholder={t('animated_layouts.search_placeholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleKeyPress}
                        // disabled={isLoading}
                        />
                        {!isEmpty(searchTerm) && (
                            <Button
                                icon="pi pi-times"
                                rounded
                                text
                                severity="primery"
                                aria-label="Cancel"
                                onClick={resetInput}
                                className="absolute inset-y-0 right-10 border-transparent bg-transparent text-black"
                            />
                        )}
                        <Button
                            icon="pi pi-search"
                            rounded
                            text
                            severity="primery"
                            aria-label="Search"
                            className="absolute inset-y-0 right-2 border-transparent bg-transparent text-black"
                            onClick={handleInput}
                            loading={isLoading}
                        />
                    </div>

                </div>

                <button type="reset" className="h-[44px] btn btn-primary "
                    disabled={isLoading}
                    onClick={() => dispatch(toggleDialog("CreateAnimationLayout"))} >
                    {t('animated_layouts.new_layout')}
                </button>
            </div>

        );
    }, [searchTerm, isLoading]);





    const subNavbarProps = useMemo(() => {
        return {
            icon: <i className="fas  fa-magic text-secondary  fs-2 "></i> || " ",
        };
    }, []);
    const totalCount = data?.pages[0]?.totalItems ?? 0;
    const layouts = data?.pages.flatMap((page) => page.items) ?? [];

    const loadAll = React.useCallback(async () => {
        if (loadingAll || isLoading) return;
        setLoadingAll(true);

        try {
            // Use the server’s own pagination info to know how many more to grab
            // Take the last page you have, figure out remaining pages, loop.
            let last = data?.pages?.[data.pages.length - 1];
            let remaining = last ? (last.totalPages - last.currentPage) : 0;

            // Fallback if we don't have data yet: just loop while hasNextPage flips true
            if (!last) {
                while (hasNextPage) {
                    await fetchNextPage();
                }
            } else {
                for (let i = 0; i < remaining; i++) {
                    await fetchNextPage();
                }
            }
        } finally {
            setLoadingAll(false);
        }
    }, [data, fetchNextPage, hasNextPage, isLoading, loadingAll]);

    if (error?.response?.status === 402) {
        return (
            <Layout>
                <FeatureLocked feature="animated_layouts" message={error.response.data?.message} />
            </Layout>
        );
    }

    return (
        <Layout subNavbarProps={subNavbarProps} subNavbarContent={subNavbarContent}>
            <div className="card h-100 w-100 flex flex-col gap-3 p-3">

                <div className="flex justify-between">
                    <small
                        onClick={loadAll}
                        className={[
                            "bg-gray-100 px-2 py-1 rounded cursor-pointer select-none",
                            (loadingAll || isLoading || isFetchingNextPage || !hasNextPage) ? "opacity-60 cursor-not-allowed" : ""
                        ].join(" ")}
                        title={t('animated_layouts.fetch_all_pages')}
                    >
                        {t('animated_layouts.all_label')}&nbsp;
                        {(isLoading || loadingAll || isFetchingNextPage) ? "..." : (data?.pages?.[0]?.totalItems ?? totalCount ?? 0)}
                    </small>
                    <div className="flex gap-1">
                        {(isFetchingNextPage || (searchTerm && isLoading)) && (
                            <div className="flex justify-end items-center h-full">
                                <ProgressSpinner style={{ width: "30px", height: "30px" }} />
                            </div>
                        )}

                    </div>
                </div>



                {isLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
                    </div>
                ) : layouts.length === 0 ? (
                    <h5 className="py-5 text-center w-full">
                        {isEmpty(searchTerm) ? "" : t('animated_layouts.no_results')}
                    </h5>
                ) : (


                    <div ref={scrollRef} style={{ maxHeight: 'calc(100vh - 220px)' }}
                        className={`  d-flex flex-column   scroll_div  overflow-x-hidden  w-100`}>
                        <div className="flex gap-3 flex-wrap mb-4">
                            {
                                layouts?.map((item, index) => {


                                    return (

                                        <div className="group border rounded-xl w-100 flex  items-center h-[85px]   p-2 "
                                            // ref={index === campaigns?.length - 1 ? observerRef : null}
                                            // ref={scrollRef}
                                            key={`${item.type} - ${index}`}

                                        >

                                            <div className={` col-lg-1 px-3 flex justify-center h-100 cursor-pointer`}
                                                onClick={() => navigate('/apps/animated_layouts/' + item.id + '/show')}

                                            >
                                                <div className=" me-2 p-4 rounded-2 bg-secondary d-flex align-items-center justify-content-center playlist_icon_container">
                                                    <i className="fas fa-table  fs-4 text-white"></i>
                                                </div>
                                            </div>
                                            <div
                                                className={` font-[400px] col-2 overflow-hidden col-lg-4 col-sm-4 mx-1 fs-6 flex-col flex justify-start items-start  cursor-pointer     `}
                                                onClick={() => {
                                                    //
                                                }}>

                                                <div className=" w-100 " ref={(el) => (inputRefs.current[item.id] = el)} >
                                                    {editingItemId === item.id ? (
                                                        <div className="relative w-75 flex items-center"  >
                                                            <InputText
                                                                value={titleValue}
                                                                onChange={(e) => setTitleValue(e.target.value)}
                                                                autoFocus
                                                                maxLength={30}
                                                                placeholder={t('animated_layouts.enter_title')}
                                                                className="w-100 pr-8 titleCampaignIndexEditor focus:border-blue-500 outline-none bg-transparent shadow-none text-sm p-0 transition-all"
                                                            />
                                                            <Button
                                                                icon="pi pi-check"
                                                                className="absolute right-0 p-button-text p-button-sm"
                                                                loading={updateLayout.isLoading && updateLayout.variables?.layoutId === item.id}
                                                                onClick={() =>
                                                                    handleSubmitTag(item, titleValue, true)
                                                                }
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className="cursor-pointer group/title flex items-center"
                                                            onClick={() => {
                                                                setEditingItemId(item.id);
                                                                setTitleValue(item.title);
                                                            }}
                                                        >
                                                            {item.title}
                                                            {updateLayout.isLoading && updateLayout.variables?.layoutId === item.id ? (
                                                                <i className="pi pi-spin pi-spinner ml-3" style={{ fontSize: "1rem" }}></i>
                                                            ) : (
                                                                <i className="pl-3 fa-solid fa-pen fa-xs opacity-0  group-hover/title:opacity-100 transition-opacity duration-150"></i>
                                                            )}
                                                        </div>
                                                    )}


                                                </div>
                                                {item.status === "INACTIVE" && (
                                                    <div className="gap-1 text-muted text-[12px] flex justify-start items-center">
                                                        <i
                                                            className={` draftedItem-${item.id} cursor-default fa-solid fa-circle-exclamation  text-[#f59e0b] `}
                                                            data-pr-position="bottom"
                                                            data-pr-tooltip={t('animated_layouts.publish_required_tooltip')}
                                                        ></i>
                                                        <div
                                                            className={`draftedItem-${item.id} cursor-default  text-[#f59e0b]`}
                                                            data-pr-position="bottom"
                                                            data-pr-tooltip={t('animated_layouts.publish_required_tooltip')}
                                                        >
                                                            {t('animated_layouts.publish_required')}
                                                        </div>
                                                        <Tooltip target={`.draftedItem-${item.id}`} showDelay={100} className="fs-8" />
                                                    </div>
                                                )}

                                            </div>

                                            <div className="col-lg-1 flex flex-col justify-center items-center  "
                                                onClick={() => navigate('/apps/animated_layouts/' + item.id + '/show')}
                                            >
                                                <div className="text-muted text-xs ">{t('animated_layouts.duration')}</div>
                                                <div className="">{formatDuration(item.layout_duration)}</div>
                                            </div>


                                            <div className="col-lg-6  col-sm-5 flex justify-end px-4 opacity-0 pointer-events-none transition-opacity duration-150
                  group-hover:opacity-100 group-hover:pointer-events-auto
                  focus-within:opacity-100">
                                                {actionBodyTemplate(item)}
                                            </div>
                                        </div>
                                    )
                                })
                            }
                        </div>

                        <div ref={sentinelRef} style={{ height: 1 }} />

                    </div>

                )}



            </div>
            {openDialog.CreateAnimationLayout && <CreateAnimationLayout />}
            {
                openDialog.updateLayout && <Update layout={layout} setReload={refetch()} />
            }
            {
                deleteDialog.layout &&
                <DeleteConfirmation
                    deleteHandler={deleteLayout}
                    itemName={layout.title}
                    dialogKey="layout"
                />
            }
        </Layout>
    )
}

export default AnimatedLayoutIndex